import React, { useState, useEffect, useRef } from "react";
import styles from "../addCategoryForm/addCategoryForm.module.css";
import { Add, Delete } from "../../Svg";
import { useSelector } from "react-redux";
import { Autocomplete, TextField } from "@mui/material";

// Searchable attribute picker, styled like the rest of the dashboard forms.
const BRAND = '#512DA8';
const attributeTypeSx = {
    marginTop: '4px',
    '& .MuiOutlinedInput-root': {
        height: '42px',
        padding: '0 10px !important',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#101828',
        background: '#FAFAFC',
        fontFamily: `'Public Sans', sans-serif`,
        transition: 'all 0.15s ease-in-out',
        '& fieldset': { borderColor: '#D0D5DD' },
        '&:hover fieldset': { borderColor: '#B9B3D6' },
        '&.Mui-focused': { background: '#FFFFFF', boxShadow: '0 0 0 3px rgba(81, 45, 168, 0.12)' },
        '&.Mui-focused fieldset': { borderColor: BRAND, borderWidth: '1.5px' },
    },
    '& .MuiAutocomplete-input': { padding: '0 4px !important' },
    '& .MuiAutocomplete-input::selection': { background: 'rgba(81, 45, 168, 0.18)' },
    '& .MuiAutocomplete-endAdornment': { right: '8px' },
    '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': { color: '#667085' },
};
const attributeTypeSlotProps = {
    paper: {
        sx: {
            marginTop: '6px',
            borderRadius: '10px',
            border: '1px solid #EAECF0',
            boxShadow: '0 12px 32px -8px rgba(16, 24, 40, 0.18)',
            fontFamily: `'Public Sans', sans-serif`,
            '& .MuiAutocomplete-listbox': { padding: '6px', maxHeight: '300px' },
            '& .MuiAutocomplete-noOptions': { fontSize: '13px', color: '#667085', padding: '12px 14px' },
            '& .MuiAutocomplete-option': {
                fontSize: '14px',
                color: '#101828',
                borderRadius: '6px',
                minHeight: '36px',
                padding: '6px 10px',
                margin: '1px 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            },
            '& .MuiAutocomplete-option.Mui-focused': { background: '#F0EEFF' },
            '& .MuiAutocomplete-option[aria-selected="true"]': { background: '#F0EEFF', color: BRAND, fontWeight: 600 },
            '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': { background: '#E6E1FB' },
            '& .MuiAutocomplete-option[aria-disabled="true"]': { opacity: 1, color: '#98A2B3' },
        },
    },
};



const VariantsCard = ({ setFieldValue, values, errors, touched, handleBlur }) => {

    const { atributes } = useSelector((state) => state.products);
    const { exportedData } = useSelector((state) => state.manufacturer);
    const manufacturer = Array.isArray(exportedData) ? exportedData : (Array.isArray(exportedData?.data) ? exportedData.data : []);
    const attributeOptions = atributes.attributeOptions
    const valueOptions = atributes.valueOptions

    const [attributes, setAttributes] = useState([
        { type: "", values: [] },
    ]);
    const [isOpen, setIsOpen] = useState(null);
    // Search text typed in each row's attribute picker (undefined = show the chosen name).
    const [searchText, setSearchText] = useState({});
    // The attributes object this component last wrote to Formik. The sync
    // effect below ignores that write, so a row whose name was just cleared
    // (it is not written to Formik) does not disappear from the screen.
    const lastWrittenRef = useRef(null);

    const writeToFormik = (rows) => {
        const next = Object.fromEntries(
            rows.filter((row) => row.type && row.type.trim() !== '').map((row) => [row.type, row.values])
        );
        lastWrittenRef.current = next;
        setFieldValue("attributes", next);
    };


    useEffect(() => {
        if (values.attributes === lastWrittenRef.current) return;
        if (values.attributes && typeof values.attributes === 'object') {
            const formattedAttributes = Object.entries(values.attributes)
                .filter(([key]) => key && key.trim() !== '')
                .map(([key, value]) => {
                    let valArr = [];
                    if (Array.isArray(value)) {
                        valArr = value;
                    } else if (value !== undefined && value !== null && value !== '') {
                        valArr = [String(value)];
                    }
                    return {
                        type: key,
                        values: valArr
                    };
                });
            setAttributes(formattedAttributes.length ? formattedAttributes : [{ type: "", values: [] }]);
        }
    }, [values.attributes]);


    const handleAttributeTypeChange = (index, newType) => {
        setAttributes((prev) => {
            const updatedAttributes = [...prev];
            let initialValues = [];
            if (newType && newType.trim().toLowerCase() === "manufacturer") {
                const selectedMfgId = values?.productOverview?.manufacturer?.[0];
                const selectedMfg = manufacturer?.find(m => m?._id?.toString() === selectedMfgId?.toString());
                if (selectedMfg?.name) {
                    initialValues = [selectedMfg.name];
                }
            }
            updatedAttributes[index] = { type: newType, values: initialValues };
            writeToFormik(updatedAttributes);
            return updatedAttributes;
        });
    };


    const handleValueChange = (index, value) => {
        setAttributes((prev) => {
            const updatedAttributes = [...prev];

            updatedAttributes[index] = { ...updatedAttributes[index], values: [value] };
            writeToFormik(updatedAttributes);
            return updatedAttributes;
        });
    };


    const handleAddAttribute = () => {
        setAttributes([...attributes, { type: "", values: [] }]);
    };

    const handleRemoveAttribute = (index) => {
        setSearchText({});
        setAttributes((prev) => {
            const updatedAttributes = prev.filter((_, i) => i !== index);
            writeToFormik(updatedAttributes);
            return updatedAttributes;
        });
    };

    return (
        <div className={styles.formSection}>
            <div className={styles.card}>
                <h2 className={styles.addCatH2}>Attributes</h2>
                {errors.attributes && touched.attributes && (
                    <p className={styles.errorText}>{errors.attributes}</p>
                )}
                {attributes?.map((attr, index) => (
                    <div key={index} className={styles.variantContainer}>

                        <label className={styles.addCatLabel} style={{ width: "50%" }}>
                            Variation Type
                            <Autocomplete
                                size="small"
                                options={
                                    attr.type && !attributeOptions?.includes(attr.type)
                                        ? [attr.type, ...(attributeOptions || [])]
                                        : (attributeOptions || [])
                                }
                                value={attr.type || null}
                                onChange={(e, newValue) => handleAttributeTypeChange(index, newValue || "")}
                                // While the list is open the box is a search field: it opens
                                // empty so typing filters at once; on close it shows the chosen name.
                                inputValue={searchText[index] !== undefined ? searchText[index] : (attr.type || "")}
                                onInputChange={(e, text, reason) => {
                                    if (reason === "input") setSearchText((prev) => ({ ...prev, [index]: text }));
                                }}
                                onOpen={() => setSearchText((prev) => ({ ...prev, [index]: "" }))}
                                onClose={() => setSearchText((prev) => { const next = { ...prev }; delete next[index]; return next; })}
                                blurOnSelect
                                onBlur={handleBlur}
                                getOptionDisabled={(option) => attributes.some(a => a.type === option && a.type !== attr.type)}
                                isOptionEqualToValue={(option, value) => option === value}
                                autoHighlight
                                noOptionsText="No matching attribute"
                                sx={attributeTypeSx}
                                slotProps={attributeTypeSlotProps}
                                renderOption={(props, option, { selected }) => {
                                    const used = attributes.some(a => a.type === option && a.type !== attr.type);
                                    const { key, ...optionProps } = props;
                                    return (
                                        <li key={key} {...optionProps}>
                                            <span>{option}</span>
                                            {selected && <span style={{ fontSize: '12px' }}>✓</span>}
                                            {used && <span style={{ fontSize: '11px', color: '#98A2B3' }}>already added</span>}
                                        </li>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder={attr.type || "Type to search attributes…"} />
                                )}
                            />
                        </label>

                        <div style={{ display: "block", marginTop: 10, width: '50%' }} className={styles.addCatLabel}>
                            Variation Value

                            <input
                                type="text"
                                placeholder="Enter value"
                                className={styles.addCatInput}
                                value={attr.values && attr.values.length > 0 ? attr.values.join(", ") : ""}
                                onChange={(e) => handleValueChange(index, e.target?.value)}
                            />

                        </div>


                        {attributes.length > 1 && (
                            <button
                                type="button"
                                className={styles.removeButton}
                                onClick={() => handleRemoveAttribute(index)}
                                style={{ border: 'none', borderRadius: 4, background: 'none' }}
                            >
                                <Delete />
                            </button>
                        )}
                    </div>
                ))}


                <button type="button" className={styles.addVar} onClick={handleAddAttribute}>
                    <Add /> Add Attribute
                </button>
            </div>
        </div>
    );
};

export default VariantsCard;
